import { AgAppConfigService } from '@ag/vc.ag-core/settings';
import { NAVIGATOR } from '@ag/vc.ag-core/tokens';
import { AgTranslateService } from '@ag/vc.ag-core/translate';
import { getCurrencySymbol } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AnimationConfigWithPath } from 'lottie-web';
import { ActiveToast, IndividualConfig, ToastrService } from 'ngx-toastr';
import { BehaviorSubject, interval, Observable, of } from 'rxjs';
import { SessionKeys } from '../models/SessionKeys';
import { RouteNames } from '../routing/app-routing.constants';
import { SilToastrService, ToastrType } from './sil-toastr.service';
const STORAGE_KEY = 'broadcastPopupFlags';
@Injectable({
  providedIn: 'root'
})
export class HelperService {
  private toastRef?: ActiveToast<unknown>;
  private messageTimeout = 1000000;
  private isGetStatusTimeout = new BehaviorSubject<boolean>(false);
  private popupHandled$ = new BehaviorSubject<boolean>(false);
  apiFailureCount: { [key: string]: number} = {
    getvalidationparameters: 0,
    retrievepersondetails: 0,
    loginuser: 0,
    issubscriptionallowed: 0,
    createuser: 0,
    getpersondetails: 0,
    gettermsofuseconsent: 0,
    isContactAndTOUAvailable: 0,
    validateperson: 0
  };
  constructor(
    private toastr: ToastrService,
    private agTranslateService: AgTranslateService,
    @Inject(NAVIGATOR) private readonly navigator: Navigator,
    private silToast: SilToastrService,
    private router: Router,
  ) {
  }

  public parseAmount(amount: string | number, decimalRequired = false): number | null {
    if (amount && typeof amount === 'string') {
      let splitSymbol: string;
      if (this.agTranslateService.currentLang === 'fr' || this.agTranslateService.currentLang === 'nl') {
        splitSymbol = ','; // checking the condition, incase any other languages come up, eg. English.
      }
      const amountArray = amount.split(splitSymbol);
      let parsedAmount = amountArray[0].replace(/[^\-0-9]/g, '');
      if(decimalRequired && amountArray[1]?.length) parsedAmount = `${parsedAmount}.${amountArray[1]}`;
      return Number(parsedAmount);
    }
    else if (amount && typeof amount === 'number') { return amount; } // fallback condition
    else { return null; }
  }
  public formatAmount(amount: number): string | null {
    // TODO: nl-be can be made dynamic or conditional
    if (amount || amount === 0) { return new Intl.NumberFormat('nl-be', { minimumFractionDigits: 2 }).format(amount); }
    return null;
  }

  public formatDecimal(value: number, decimalPlace = 2): string | undefined {
    if (value) {
      const roundOffValue = value.toFixed(decimalPlace);
      return new Intl.NumberFormat('nl-be').format(Number(roundOffValue));
    }
    return undefined;
  }

  public ParseAmountWithCommaSeperator(amount: string | number): string | undefined {
    if (amount && typeof amount === 'string') {
      let splitSymbol: string;
      if (this.agTranslateService.currentLang === 'fr' || this.agTranslateService.currentLang === 'nl') {
        splitSymbol = ','; // checking the condition, incase any other languages come up, eg. English.
      }
      const amountArray = amount.split(splitSymbol);
      let parsedAmount = amountArray[0].replace(/[^\-0-9]/g, '');
      if(Number(amountArray[1]) > 0) parsedAmount = `${parsedAmount},${amountArray[1]}`;
      return parsedAmount;
    } else {
      return undefined;
    }
  }

  public showErrorMessage(message: string): void {
    this.clearErrorMessage();
    this.clearSuccessMessage();
    this.toastRef = this.toastr.error(message, '', this.getToastrSettings('error'));
  }

  public showSuccessMessage(message: string): void {
    this.clearErrorMessage();
    this.clearSuccessMessage();
    this.toastRef = this.toastr.success(message, '', this.getToastrSettings('success'));
  }

  private getToastrSettings(type: 'success' | 'warning' | 'error'): Partial<IndividualConfig> {
    return {
      timeOut: this.messageTimeout,
      extendedTimeOut: this.messageTimeout,
      enableHtml: true,
      tapToDismiss: false,
      toastClass: `ngx-toastr sil-${type}-toastr`,
      closeButton: false
    } as IndividualConfig;
  }

  public changeErrorMessage(message: string): void {
    this.changeToastrMessage(message);
  }

  public changeSuccessMessage(message: string): void {
    this.changeToastrMessage(message);
  }

  private changeToastrMessage(message: string): void {
    const toastrlength = this.toastr.toasts.length;
    if (toastrlength > 0) {
      if (toastrlength !== 1) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const componentInstance = this.toastr?.toasts.find(toast => toast.toastId === 1)?.toastRef.componentInstance;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (componentInstance) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          componentInstance.message = message;
        }
      }
      else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const componentInstance = this.toastr.toasts[0].toastRef.componentInstance;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        componentInstance.message = message;
      }
    }
  }

  public clearErrorMessage(): void {
    if (this.toastRef) {
      this.toastr.clear(this.toastRef.toastId);
    }
  }

  public clearSuccessMessage(): void {
    if (this.toastRef) {
      this.toastr.clear(this.toastRef.toastId);
    }
  }

  public getIPAddress() {
    // return this.http.get('https://api.ipify.org/?format=json');
    return of({ ip: '193.202.17.249' });
  }

  public checkIfRecentDate(date: string): boolean {
    const simulationDate = new Date(date);
    const presentDate = new Date();
    const simulationTime = simulationDate.getTime();
    const presentTime = presentDate.getTime();
    return ((presentTime - simulationTime) / (24 * 3600 * 1000) <= 365) ? true : false;
  }

  public getElementPosition(el: ElementRef<HTMLElement>): { top: number, left: number } {
    const rect = el.nativeElement.getBoundingClientRect();
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
  }

  public getLottieConfig(
    lottieContainer: HTMLElement | Element,
    isLoop: boolean,
    isAutoplay: boolean,
    animationName: string,
    animationPath: string,
    AnimationSegment?: [number, number]): AnimationConfigWithPath {
    if (lottieContainer?.childNodes?.length) {
      lottieContainer.removeChild(lottieContainer.childNodes[0]);
    }
    return {
      container: lottieContainer, // the dom element that will contain the animation
      loop: isLoop,
      autoplay: isAutoplay,
      name: animationName,
      path: animationPath, // the path to the animation json
      initialSegment: AnimationSegment
    } as AnimationConfigWithPath;
  }

  public getEncodedPhoneNumber(PhoneNumber: string, isformat = false, isMask = false): string | null {
    let NumberWithoutCode = '';
    if (!PhoneNumber) {
      return null;
    }
    if (PhoneNumber.startsWith('0')) {
      NumberWithoutCode = PhoneNumber.slice(1, PhoneNumber.length);
    } else if (PhoneNumber.startsWith('+32')) {
      //Bug48554- Remove '0' if Number Starts with '+320'
      NumberWithoutCode = PhoneNumber.slice(3, PhoneNumber.length).startsWith('0') ? PhoneNumber.slice(4, PhoneNumber.length) : PhoneNumber.slice(3, PhoneNumber.length);
    } else {
      NumberWithoutCode = PhoneNumber;
    }
    NumberWithoutCode = NumberWithoutCode.replace(/\s+/g, '');
    if (isformat) { // will return phone number in +32 xxx xxx xxx
      if (isMask) {
        NumberWithoutCode = [...NumberWithoutCode].map((d, i) => (i) > 1 ? d.replace(/[0-9]/g, 'X') : d).join('').trim();
      }
      NumberWithoutCode = [...NumberWithoutCode].map((d, i) => (i) % 3 === 0 ? ' ' + d : d).join('').trim();
      return `+32 ${NumberWithoutCode}`;
    }
    else { // will return in +32XXXXXXXXX
      if (NumberWithoutCode.startsWith('+91') && AgAppConfigService.get<string>('isIndianNumberAllowed') === 'true') {
        return NumberWithoutCode;
      }
      return `+32${NumberWithoutCode}`;
    }
  }

  public getParameterByName(name: string, url = window.location.href): string | null {
    // eslint-disable-next-line no-useless-escape
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  public createSession(keyName: string, data: string): void {
    sessionStorage.setItem(keyName, data);
  }

  public getSessionValue(keyName: string): string | null {
    return sessionStorage.getItem(keyName);
  }

  public clearSession(keyName: string): void {
    sessionStorage.removeItem(keyName);
  }

  public createStorage(keyName: string, data: string): void {
    localStorage.setItem(keyName, data);
  }

  public getStorageValue(keyName: string): string | null {
    return localStorage.getItem(keyName);
  }

  public clearStorage(keyName: string): void {
    localStorage.removeItem(keyName);
  }

  public formatIbanNumber(ibannumber: string, withcountrycode = true): string {
    if (ibannumber) {
      const iban = ibannumber.replace(/\s+/g, '');
      if (withcountrycode && iban.startsWith('BE')) {
        return iban;
      }
      else {
        return withcountrycode ? `BE${iban}` : iban.slice(2, iban.length);
      }
    }
    return '';
  }

  public formatPPR(value: string): string | null {
    if (value?.length) {
      return value.slice(0, 3) + '/' + value.slice(3, 7) + '/' + value.slice(7, value.length);
    }
    return null;
  }

  public formatPhoneNumber(PhoneNumber: string, withZero = false, withSpace = false): string {
    let NumberWithoutCode = '';
    if (PhoneNumber.startsWith('0')) {
      NumberWithoutCode = PhoneNumber.slice(1, PhoneNumber.length);
    } else if (PhoneNumber.startsWith('+32')) {
      //Bug48554- Remove '0' if Number Starts with '+320'
      NumberWithoutCode = PhoneNumber.slice(3, PhoneNumber.length).startsWith('0') ? PhoneNumber.slice(4, PhoneNumber.length) : PhoneNumber.slice(3, PhoneNumber.length);
    } else {
      NumberWithoutCode = PhoneNumber;
    }
    NumberWithoutCode = NumberWithoutCode.replace(/\s+/g, '');
    if (NumberWithoutCode.startsWith('+91') && AgAppConfigService.get<string>('isIndianNumberAllowed') === 'true') {
      return NumberWithoutCode;
    }
    else { // will return in +32XXXXXXXXX
      NumberWithoutCode = NumberWithoutCode.slice(0, 3) + ' ' + NumberWithoutCode.slice(3, 5) + ' ' + NumberWithoutCode.slice(5, 7) + ' ' + NumberWithoutCode.slice(7, 9);
      if (withZero) {
        return `+32(0) ${NumberWithoutCode}`;
      } else if (withSpace) {
        return `+32 ${NumberWithoutCode}`;
      }
      else {
        return `+32${NumberWithoutCode}`;
      }
    }
  }

  public isElementinViewport(el: HTMLElement, withHeaderFooter = true): boolean { // excluding the height of the footer and header of our application
    const position = el.getBoundingClientRect();
    let fixedScreenElementHeight = 60; //  header or footer
    if (!withHeaderFooter) {
      fixedScreenElementHeight = 0;
    }
    if (position.top >= 0 + fixedScreenElementHeight && position.bottom <= window.innerHeight - (fixedScreenElementHeight * 2)) { // full visibility
      return true;
    }
    if (position.top < window.innerHeight - (fixedScreenElementHeight * 2) && position.bottom >= 0 + fixedScreenElementHeight) { // partial visibility
      return true;
    } else { // invisible
      return false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public formatCurrencyAmount(value: number, curreny = 'EUR', amountFormat = false, displaySign = false, withoutDecimal = false, innerHtmlRequired = false, roundOff = false): string {
    let formatValue: string | undefined = '';
    const currencySymbol = getCurrencySymbol(curreny, 'narrow');
    if (roundOff && withoutDecimal) {
      value = Math.round(value);
    }

    if (value || (Math.abs(value)===0 && value !== null && typeof(value)!=='undefined'))
    {
      if (amountFormat) {
        formatValue = this.formatAmount(value);
        if (withoutDecimal) {
          formatValue = formatValue?.toString().split(',')?.[0];
        }
      }
      const splitarray = amountFormat ? formatValue?.toString().split(',') : value.toString().split('.');
      if (splitarray) {
        const amount = splitarray[0];
        const decimal = splitarray[1] && Number(splitarray[1]) > 0 ? splitarray[1].substring(0, 2).trim() : '00';
        if (displaySign) {
          const signValue = Number(amount) > 0 ? '+' : (Number(amount) < 0 ? '-' : (amount === '-0' ? '-' : ''));
          const finalAmount = Number(amount) < 0 || amount === '-0' ? (Number(amount.replace('.', '')) * -1).toLocaleString('nl-be') :  amount;
          if (withoutDecimal) {
            return `${signValue} ${currencySymbol} ${finalAmount}`;
          }
          else {
            if (innerHtmlRequired) {
              return `${signValue} ${currencySymbol} ${finalAmount},<i>${decimal}</i>`;
            }
            else {
              return `${signValue} ${currencySymbol} ${finalAmount},${decimal}`;
            }

          }
        }
        if (innerHtmlRequired) {
          return `${currencySymbol} ${amount},<i>${decimal}</i>`;
        }
      }
    }
    return `${currencySymbol} ${formatValue}`;
  }

  // Just a layer so that the method can be used in html templates
  public abs(value: number): number {
    return Math.abs(value);
  }

  public copyToClipboard(value: string | number, event: MouseEvent): void {
    void this.navigator.clipboard.writeText(value.toString());

    const clickedElem = event.currentTarget as HTMLElement;
    const toolTipDiv = document.createElement('div');
    toolTipDiv.className = 'copy-success'
    toolTipDiv.innerText = this.agTranslateService.currentLang === 'fr' ? 'Copié dans le presse papier !' : 'Gekopieërd naar klembord!'
    clickedElem.prepend(toolTipDiv);
    setTimeout(() => {
      toolTipDiv.remove();
    }, 3000);
  }

  public otpValidityTimer(): Observable<number> {
    return interval(1000);
  }
//Below method is to trim any sort of leading and trailing spaces from any string values in an object (or any complex object for that matter)
  public trimStringValues(obj: unknown): unknown{
    if (Array.isArray(obj)){
      // eslint-disable-next-line @typescript-eslint/unbound-method
      return obj.map(this.trimStringValues);
    }else if (typeof obj === 'object' && obj){
      for (const key in obj){
        obj[key] = this.trimStringValues(obj[key]);
      }
    }else if(typeof obj === 'string'){
      obj= obj.trim();
    }
    return obj;
  }

  public translateText(key: string): string {
    let translatedText: string;
    this.agTranslateService.get(key).subscribe((res: string) => {
      translatedText = res;
    });
    return translatedText;
  }

  public translate(key: string, value : {[key: string]: string}): string {
    let translatedText: string;
    if(value){
      this.agTranslateService.get(key, value).subscribe((res: string) => {
        translatedText = res;
      });
    }   
    return translatedText;
  }
  public setOnlinePaymentGetStatusTimeOut(flag: boolean): void {
    this.isGetStatusTimeout.next(flag);
  }

  public getOnlinePaymentGetStatusTimeOut(): Observable<boolean> {
    return this.isGetStatusTimeout.asObservable();
  }

showGenericErrorToast(){
  this.silToast.showToast('Generic.ErrorMessage', ToastrType.error);
}

handleAPIFailure(api: string, status?: number): void {
  if(status === 401) {
    this.router.navigate(['/' + RouteNames.authentication + '/' + RouteNames.logout]);
  } else {
    this.apiFailureCount[api]++;
    if(this.apiFailureCount[api] === 1){
      this.clearSession(SessionKeys.isFromComeBackLater);
      void this.router.navigate(['/' + RouteNames.ErrorScreen], { state: { api } 
      });
    } else {
      void this.router.navigate(['/' + RouteNames.ComeBackLater]);
    }
    }
  }

  
  getPopupFlags(): Record<string, boolean> {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  }
  isPoupShown(route: string): boolean {
    const flags = this.getPopupFlags();
    return !!flags[route];
  }
  markPopupShown(route: string): void {
    const flags = this.getPopupFlags();
    flags[route] = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  }
  setPopupHandledStatus(): void {
    this.popupHandled$.next(true);
  }
  getPopupHandledStatus() {
    return this.popupHandled$.asObservable();
  }
  clearAllPopupShownFlags(): void  {
    localStorage.removeItem(STORAGE_KEY);
  }  
  isPageReload(): boolean {
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (!navEntries.length) return false;
  
    const isReload = navEntries[0].type === 'reload' || navEntries[0].type === 'navigate';
    console.log(`Page reload detected: ${isReload}`);
    console.log(navEntries);
  
    return isReload;
  }
  
}